import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../i18n';
import { productionAPI } from '../api';
import toast from 'react-hot-toast';

// История ввода. Справочника заранее нет: человек пишет значение руками, оно
// запоминается НА СЕРВЕРЕ и со следующего раза предлагается всем — в любом
// браузере и на любом компьютере. Часто используемые поднимаются наверх,
// ненужное убирается крестиком.
//
// Общий компонент: одним и тем же полем пользуются и таблица согласования,
// и правка выданной заявки — подсказки там обязаны быть одни и те же.
//
// Список рисуем НЕ внутри строки таблицы, а поверх страницы. Таблица
// согласования широкая и лежит в блоке с прокруткой вбок; браузер на такой
// блок включает и прокрутку вниз — и всё, что вылезает за его нижний край,
// обрезается. Когда в очереди четыре позиции, блок высотой в четыре строки, и
// подсказки («чфмк», «Влагостойка») обрезались ровно там, где начинались:
// технолог видел половину первой строки и не понимал, что список вообще есть.
// size: 'sm' — ячейка таблицы согласования, где строк три десятка и каждая
// точка на счету; 'md' — обычная форма, где поле стоит рядом с прочими и
// обязано выглядеть так же, как они.
export default function SuggestInput({ value, onChange, kind, placeholder,
                                       width, label, size = 'sm' }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [rect, setRect] = useState(null);   // где на экране стоит поле
  const boxRef  = useRef(null);
  const listRef = useRef(null);

  const load = useCallback(() => {
    productionAPI.getSuggestions(kind)
      .then(r => setList(r.data.results || []))
      .catch(() => {});
  }, [kind]);

  // Список висит поверх страницы и сам за полем не поедет — пересчитываем его
  // место при прокрутке и смене размера окна.
  const place = useCallback(() => {
    if (!boxRef.current) return;
    const el = boxRef.current.querySelector('input') || boxRef.current;
    const r = el.getBoundingClientRect();
    setRect({ left: r.left, top: r.bottom, bottom: r.top, width: r.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    load();
    place();
    const onDoc = (e) => {
      const inField = boxRef.current && boxRef.current.contains(e.target);
      const inList  = listRef.current && listRef.current.contains(e.target);
      if (!inField && !inList) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, load, place]);

  const remove = async (e, id) => {
    e.stopPropagation();
    try {
      await productionAPI.deleteSuggestion(id);
      setList(prev => prev.filter(s => s.id !== id));
    } catch { toast.error(t('Не удалось убрать подсказку')); }
  };

  const MAX = 220;
  // Внизу экрана места может не остаться — тогда открываем вверх, а не
  // прижимаем список к нижнему краю окна.
  const dropUp = rect && (window.innerHeight - rect.top) < MAX + 16;

  const big = size === 'md';

  return (
    <div ref={boxRef} style={{ position: 'relative', width: width || '100%' }}>
      {label && (
        <label style={{ display: 'block', color: 'var(--muted)',
                        fontSize: 12, marginBottom: 4 }}>{label}</label>
      )}
      <input
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        style={{
          width: '100%', boxSizing: 'border-box', color: 'var(--text)',
          border: '1px solid var(--border)',
          ...(big
            ? { padding: '9px 12px', fontSize: 14, borderRadius: 8,
                background: 'var(--input)', outline: 'none' }
            : { padding: '4px 6px', fontSize: 12, borderRadius: 5,
                background: 'var(--bg)' }),
        }}
      />
      {open && list.length > 0 && rect && createPortal(
        <div ref={listRef} style={{
          position: 'fixed', left: rect.left, width: rect.width, zIndex: 9000,
          ...(dropUp ? { bottom: window.innerHeight - rect.bottom + 2 }
                     : { top: rect.top + 2 }),
          background: 'var(--card)', border: '1px solid var(--border)',
          borderRadius: 6, maxHeight: MAX, overflowY: 'auto',
          boxShadow: '0 10px 26px #0007',
        }}>
          {list.map(s => (
            <div key={s.id}
              onClick={() => { onChange(s.value); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '7px 9px',
                cursor: 'pointer', fontSize: 12.5, color: 'var(--text)',
                borderBottom: '1px solid var(--border)',
              }}>
              <span style={{ flex: 1 }}>{s.value}</span>
              <span
                onClick={e => remove(e, s.id)}
                title={t('Убрать подсказку')}
                style={{ color: '#ef4444', fontSize: 13, padding: '0 3px' }}
              >✕</span>
            </div>
          ))}
        </div>,
        document.body)}
    </div>
  );
}
